/**
 * Sponsorship tracker — per-channel sponsor deals (brand, amount, deadline,
 * approval, disclosure, deliverables).
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Briefcase, Loader2, Plus, Save, Check, X } from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import "./studio.css";

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function StudioSponsorships() {
  const { channelId } = useParams();
  const [items, setItems] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({
    brand_name: "", contact_name: "", contact_email: "",
    amount_inr: "", deadline: "", deliverables: "",
  });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/api/studio/sponsorships/${channelId}`, { withCredentials: true });
      setItems(r.data.items || []);
    } catch (_) { setItems([]); }
  };
  useEffect(() => { load(); }, [channelId]);

  const create = async () => {
    if (!form.brand_name.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/api/studio/sponsorships`, {
        channel_id: channelId,
        brand_name: form.brand_name.trim(),
        contact_name: form.contact_name || null,
        contact_email: form.contact_email || null,
        amount_inr: form.amount_inr ? Number(form.amount_inr) : null,
        deadline: form.deadline || null,
        deliverables: form.deliverables ? form.deliverables.split(",").map((s) => s.trim()).filter(Boolean) : [],
      }, { withCredentials: true });
      toast.success("Sponsorship added");
      setForm({ brand_name: "", contact_name: "", contact_email: "", amount_inr: "", deadline: "", deliverables: "" });
      setShowNew(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const toggle = async (s, key, value) => {
    try {
      await axios.patch(`${API}/api/studio/sponsorships/${s._id}`, { [key]: value }, { withCredentials: true });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };

  return (
    <>
      <Header />
      <div className="studio-shell">
        <Link to={`/studio/channels/${channelId}`} className="studio-back" data-testid="spon-back">
          <ArrowLeft size={14}/> Back to channel
        </Link>
        <div className="studio-section-h" style={{ marginTop: 12 }}>
          <div>
            <span className="studio-eyebrow"><Briefcase size={12}/> Sponsorships</span>
            <h2 style={{ margin: "6px 0 0" }}>Brand deals on this channel</h2>
          </div>
          <button className="studio-btn-primary" onClick={() => setShowNew((x) => !x)} data-testid="spon-add">
            <Plus size={14}/> Add sponsorship
          </button>
        </div>

        {showNew && (
          <div className="studio-card" style={{ marginTop: 14 }} data-testid="spon-form">
            <div className="studio-form">
              <Field label="Brand name *">
                <input value={form.brand_name} onChange={(e) => setForm({ ...form, brand_name: e.target.value })} data-testid="spon-brand"/>
              </Field>
              <div className="studio-form-row">
                <Field label="Contact name"><input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })}/></Field>
                <Field label="Contact email"><input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })}/></Field>
              </div>
              <div className="studio-form-row">
                <Field label="Amount (₹)"><input type="number" value={form.amount_inr} onChange={(e) => setForm({ ...form, amount_inr: e.target.value })}/></Field>
                <Field label="Deadline"><input type="date" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })}/></Field>
              </div>
              <Field label="Deliverables (comma-separated)">
                <input placeholder="1 long video, 3 shorts, 1 IG reel" value={form.deliverables} onChange={(e) => setForm({ ...form, deliverables: e.target.value })}/>
              </Field>
              <div className="studio-form-actions">
                <button className="studio-btn-ghost" onClick={() => setShowNew(false)}>Cancel</button>
                <button className="studio-btn-primary" onClick={create} disabled={busy || !form.brand_name.trim()} data-testid="spon-save">
                  {busy ? <Loader2 size={14} className="ac-spin"/> : <Save size={14}/>} Save
                </button>
              </div>
            </div>
          </div>
        )}

        <div style={{ marginTop: 18 }}>
          {items === null ? (
            <div className="studio-card studio-loading">
              <Loader2 size={22} className="ac-spin" style={{ color: "var(--st-purple, #a78bfa)" }}/>
            </div>
          ) : items.length === 0 ? (
            <div className="studio-card"><p className="studio-muted">No sponsorships yet on this channel.</p></div>
          ) : (
            <div className="studio-card">
              <table className="studio-table" data-testid="spon-table">
                <thead>
                  <tr>
                    <th>Brand</th><th>Amount</th><th>Deadline</th><th>Status</th><th>Approved</th><th>Paid</th><th>Linked</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((s) => (
                    <tr key={s._id} data-testid={`spon-row-${s._id}`}>
                      <td><strong style={{ color: "#0F172A" }}>{s.brand_name}</strong>
                        {s.contact_email && <><br/><small className="studio-muted">{s.contact_email}</small></>}
                      </td>
                      <td>{s.amount_inr ? `₹${Number(s.amount_inr).toLocaleString("en-IN")}` : "—"}</td>
                      <td>{s.deadline || "—"}</td>
                      <td>
                        <select
                          value={s.status || "active"}
                          onChange={(e) => toggle(s, "status", e.target.value)}
                          className="studio-stage-select"
                          data-testid={`spon-status-${s._id}`}
                        >
                          <option value="active">Active</option>
                          <option value="completed">Completed</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      </td>
                      <td>
                        <button
                          className="studio-btn-ghost"
                          onClick={() => toggle(s, "approved", !s.approved)}
                          data-testid={`spon-approved-${s._id}`}
                        >
                          {s.approved ? <Check size={14}/> : <X size={14}/>} {s.approved ? "Yes" : "No"}
                        </button>
                      </td>
                      <td>
                        <button
                          className="studio-btn-ghost"
                          onClick={() => toggle(s, "paid", !s.paid)}
                          data-testid={`spon-paid-${s._id}`}
                        >
                          {s.paid ? <Check size={14}/> : <X size={14}/>} {s.paid ? "Yes" : "No"}
                        </button>
                      </td>
                      <td>{(s.linked_content_ids || []).length}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
}

function Field({ label, children }) {
  return (
    <div className="studio-field">
      <label>{label}</label>
      {children}
    </div>
  );
}
